import type {
  ClientDetailRecord,
  CustomerMonthOption,
  CustomerReview,
  CustomerReviewAnswer,
  CustomerStatus,
  CustomerSummary,
  VisitorEntry,
} from './types';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const RATING_CRITERIA_LABELS: Array<{
  key: 'service_quality' | 'staff_behavior' | 'cleanliness' | 'waiting_time' | 'value_for_money';
  label: string;
}> = [
  { key: 'service_quality', label: 'Service Quality' },
  { key: 'staff_behavior', label: 'Staff Behavior' },
  { key: 'cleanliness', label: 'Cleanliness & Hygiene' },
  { key: 'waiting_time', label: 'Waiting Time' },
  { key: 'value_for_money', label: 'Value for Money' },
];

const RECOMMEND_LABELS: Record<number, string> = {
  1: 'Not likely at all',
  2: 'Unlikely',
  3: 'Neutral',
  4: 'Likely',
  5: 'Very likely',
};

export function buildFeedbackAnswers(
  feedback: NonNullable<ClientDetailRecord['appointments'][number]['feedback']>
): CustomerReviewAnswer[] {
  const answers: CustomerReviewAnswer[] = [];
  const selectedServices = Array.isArray(feedback.services)
    ? feedback.services.filter((service) => typeof service === 'string' && service.trim())
    : [];

  if (selectedServices.length > 0) {
    answers.push({
      question: 'Which services did you receive?',
      answer: selectedServices.join(', '),
    });
  }

  const ratings = feedback.ratings;
  if (ratings) {
    RATING_CRITERIA_LABELS.forEach(({ key, label }) => {
      const value = ratings[key];
      if (typeof value === 'number' && value >= 1) {
        answers.push({
          question: label,
          answer: String(value),
        });
      }
    });
  }

  if (typeof feedback.recommend === 'number' && feedback.recommend >= 1) {
    answers.push({
      question: 'How likely are you to recommend Slot Look?',
      answer: RECOMMEND_LABELS[feedback.recommend] ?? String(feedback.recommend),
    });
  }

  if (feedback.liked_most?.trim()) {
    answers.push({
      question: 'What did you like the most?',
      answer: feedback.liked_most.trim(),
    });
  }

  if (feedback.comment?.trim()) {
    answers.push({
      question: 'Additional comments',
      answer: feedback.comment.trim(),
    });
  }

  return answers;
}

export function normalizeCustomerKey(phone: string) {
  const digits = phone.replace(/\D/g, '');

  return digits || phone.trim().toLowerCase();
}

export function formatCustomerStatusLabel(status: CustomerStatus): string {
  switch (status) {
    case 'follow_up':
      return 'Follow up';
    case 'new':
      return 'New';
    case 'not_answering':
      return 'Not answering';
    case 'irrelevant':
      return 'Irrelevant';
    case 'junk':
      return 'Junk';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

export function getCustomerStatusTone(status: CustomerStatus) {
  switch (status) {
    case 'follow_up':
      return 'success';
    case 'new':
      return 'accent';
    case 'not_answering':
      return 'warning';
    case 'irrelevant':
      return 'muted';
    case 'junk':
      return 'danger';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

export function formatMonthKey(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
}

export function formatMonthLabel(value: Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(value);
}

function parseDate(value: string) {
  const stamp = new Date(value);

  return Number.isNaN(stamp.getTime()) ? null : stamp;
}

function getFeedbackReferenceDate(visitor: VisitorEntry) {
  return visitor.feedbackSentAt ?? visitor.createdAt;
}

export function deriveCustomerStatus(visitor: VisitorEntry, referenceDate = new Date()): CustomerStatus {
  if (visitor.customerStatusOverride) {
    return visitor.customerStatusOverride;
  }

  if (visitor.feedbackDeliveryStatus === 'failed') {
    return 'junk';
  }

  if (
    visitor.feedbackRespondedAt ||
    (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0) ||
    Boolean(visitor.feedbackNote?.trim())
  ) {
    return 'follow_up';
  }

  const sentAt = parseDate(getFeedbackReferenceDate(visitor));

  if (!sentAt) {
    return 'new';
  }

  return referenceDate.getTime() - sentAt.getTime() <= SEVEN_DAYS_MS ? 'new' : 'not_answering';
}

export function buildCustomerSummaries(visitors: VisitorEntry[], referenceDate = new Date()) {
  const grouped = new Map<string, VisitorEntry[]>();

  visitors.forEach((visitor) => {
    const key = normalizeCustomerKey(visitor.visitorPhone);
    const bucket = grouped.get(key) ?? [];

    bucket.push(visitor);
    grouped.set(key, bucket);
  });

  return Array.from(grouped.entries())
    .map(([key, group]) => {
      const visits = [...group].sort((left, right) => {
        return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      });
      const latestVisit = visits[0];
      const status = deriveCustomerStatus(latestVisit, referenceDate);
      const reviews = visits
        .filter(
          (visit) =>
            visit.feedbackRespondedAt ||
            typeof visit.feedbackRating === 'number' ||
            Boolean(visit.feedbackNote?.trim())
        )
        .map((visit) => ({
          id: `${visit.id}-review`,
          visitId: visit.id,
          createdAt: visit.feedbackRespondedAt ?? visit.createdAt,
          rating: typeof visit.feedbackRating === 'number' ? visit.feedbackRating : null,
          note: visit.feedbackNote?.trim() || null,
          answers: [] as CustomerReviewAnswer[],
          visibleToClient: visit.feedbackVisibleToClient ?? true,
          staffName: visit.staffName,
          serviceSummary: visit.services.join(' · '),
        }))
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

      return {
        key,
        name: latestVisit.visitorName,
        phone: latestVisit.visitorPhone,
        visits,
        latestVisit,
        totalVisits: visits.length,
        totalSpent: visits.reduce((sum, visit) => sum + visit.totalAmount, 0),
        firstVisitAt: visits[visits.length - 1].createdAt,
        latestVisitAt: latestVisit.createdAt,
        latestServices: latestVisit.services,
        status,
        statusLabel: formatCustomerStatusLabel(status),
        statusTone: getCustomerStatusTone(status),
        reviews,
        feedbackSentCount: visits.length,
      } satisfies CustomerSummary;
    })
    .sort((left, right) => new Date(right.latestVisitAt).getTime() - new Date(left.latestVisitAt).getTime());
}

export function collectCustomerMonths(visitors: VisitorEntry[]) {
  const monthMap = new Map<string, CustomerMonthOption>();

  visitors.forEach((visitor) => {
    const stamp = parseDate(visitor.createdAt);

    if (!stamp) {
      return;
    }

    const key = formatMonthKey(stamp);

    if (!monthMap.has(key)) {
      monthMap.set(key, {
        key,
        label: formatMonthLabel(stamp),
      });
    }
  });

  return Array.from(monthMap.values()).sort((left, right) => right.key.localeCompare(left.key));
}

export function formatCustomerVisitDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

export function formatCustomerVisitTime(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function toIsoString(value: unknown) {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  if (value) {
    const stamp = new Date(String(value));
    if (!Number.isNaN(stamp.getTime())) {
      return stamp.toISOString();
    }
  }

  return new Date().toISOString();
}

function isCustomerStatus(value: unknown): value is CustomerStatus {
  return (
    value === 'new' ||
    value === 'follow_up' ||
    value === 'not_answering' ||
    value === 'irrelevant' ||
    value === 'junk'
  );
}

export function mapClientDetailToSummary(detail: ClientDetailRecord): CustomerSummary {
  const client = detail.client;
  const status = isCustomerStatus(client.customer_status) ? client.customer_status : 'new';
  const key = normalizeCustomerKey(client.key ?? client.number);
  const visits: VisitorEntry[] = detail.appointments
    .filter((appointment) => appointment.status !== 'cancelled')
    .map((appointment) => {
      const hasFeedback = Boolean(appointment.feedback);
      const feedbackNote =
        appointment.feedback?.comment?.trim() ||
        appointment.feedback?.liked_most?.trim() ||
        appointment.feedback?.feedback?.trim() ||
        null;
      const createdAt = toIsoString(appointment.date);

      return {
        id: appointment.id,
        visitorName: client.name,
        visitorPhone: client.number,
        services: appointment.services,
        staffId: appointment.staff_id,
        staffName: appointment.staff_name,
        totalAmount: appointment.total_amount,
        createdAt,
        createdByRole: 'enterprise' as const,
        createdByName: appointment.staff_name,
        feedbackSentAt: createdAt,
        feedbackDeliveryStatus: hasFeedback || appointment.feedback_flag ? 'sent' : 'pending',
        feedbackRespondedAt: hasFeedback
          ? toIsoString(appointment.feedback?.created_at ?? appointment.date)
          : null,
        feedbackRating: appointment.feedback?.rating ?? null,
        feedbackNote,
        feedbackVisibleToClient: true,
        customerStatusOverride: status,
      } satisfies VisitorEntry;
    })
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

  const latestVisit = visits[0];
  const reviews: CustomerReview[] = detail.appointments
    .filter((appointment) => appointment.status !== 'cancelled' && appointment.feedback)
    .map((appointment) => {
      const feedback = appointment.feedback!;
      const note =
        feedback.comment?.trim() ||
        feedback.liked_most?.trim() ||
        feedback.feedback?.trim() ||
        null;

      return {
        id: `${appointment.id}-review`,
        visitId: appointment.id,
        createdAt: toIsoString(feedback.created_at ?? appointment.date),
        rating: typeof feedback.rating === 'number' ? feedback.rating : null,
        note,
        answers: buildFeedbackAnswers(feedback),
        visibleToClient: true,
        staffName: appointment.staff_name,
        serviceSummary: appointment.services.join(' · '),
      };
    })
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());

  if (!latestVisit) {
    const latestVisitAt = toIsoString(detail.summary.latest_visit_at ?? detail.summary.first_visit_at);
    const placeholderVisit: VisitorEntry = {
      id: `client-${client.id}-latest`,
      visitorName: client.name,
      visitorPhone: client.number,
      services: detail.summary.latest_services ?? [],
      staffId: 'staff',
      staffName: detail.summary.latest_staff_name ?? 'Staff',
      totalAmount: Math.round(Number(detail.summary.total_spent) || 0),
      createdAt: latestVisitAt,
      createdByRole: 'enterprise',
      createdByName: detail.summary.latest_staff_name ?? 'Staff',
      feedbackSentAt: latestVisitAt,
      feedbackDeliveryStatus: typeof detail.summary.latest_rating === 'number' ? 'sent' : 'pending',
      feedbackRespondedAt: typeof detail.summary.latest_rating === 'number' ? latestVisitAt : null,
      feedbackRating: detail.summary.latest_rating ?? null,
      feedbackNote: null,
      feedbackVisibleToClient: true,
      customerStatusOverride: status,
    };

    return {
      key,
      name: client.name,
      phone: client.number,
      visits: [placeholderVisit],
      latestVisit: placeholderVisit,
      totalVisits: detail.summary.appointment_count,
      totalSpent: Math.round(Number(detail.summary.total_spent) || 0),
      firstVisitAt: toIsoString(detail.summary.first_visit_at ?? latestVisitAt),
      latestVisitAt,
      latestServices: detail.summary.latest_services ?? [],
      status,
      statusLabel: formatCustomerStatusLabel(status),
      statusTone: getCustomerStatusTone(status),
      reviews,
      feedbackSentCount: detail.summary.appointment_count,
    };
  }

  return {
    key,
    name: client.name,
    phone: client.number,
    visits,
    latestVisit,
    totalVisits: detail.summary.appointment_count,
    totalSpent: Math.round(Number(detail.summary.total_spent) || 0),
    firstVisitAt: toIsoString(detail.summary.first_visit_at ?? latestVisit.createdAt),
    latestVisitAt: toIsoString(detail.summary.latest_visit_at ?? latestVisit.createdAt),
    latestServices: detail.summary.latest_services.length
      ? detail.summary.latest_services
      : latestVisit.services,
    status,
    statusLabel: formatCustomerStatusLabel(status),
    statusTone: getCustomerStatusTone(status),
    reviews,
    feedbackSentCount: visits.length,
  };
}

export type { CustomerReview, CustomerStatus, CustomerSummary, VisitorEntry } from './types';
